// P1B-FE FE-1 — NVR topology: Site → Node (SLink agent) → Source (NVR) → channels.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ServerIcon,
  HardDriveIcon,
  VideoIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  SearchIcon,
} from "lucide-react";

import {
  useTopology,
  type TopologyResponse,
  type TopologySource,
} from "@/api/topology";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

// Batch 1 (UX/IA) — client-side search across the tree. A channel matches on
// its name; a source on host; a node on name/agent; a site on name. Ancestors
// of any match are kept so the path stays navigable. Returns the narrowed tree
// (empty q → unchanged).
type Site = TopologyResponse["sites"][number];
function filterTopology(sites: Site[], q: string): Site[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return sites;
  const has = (s: string | null | undefined) =>
    (s ?? "").toLowerCase().includes(needle);
  const out: Site[] = [];
  for (const site of sites) {
    const nodes = [];
    for (const node of site.nodes) {
      const nodeHit = has(node.name) || has(node.agent_name);
      const sources = [];
      for (const src of node.sources) {
        const srcHit = has(src.host) || has(src.protocol);
        const channels =
          srcHit || nodeHit || has(site.name)
            ? src.channels
            : src.channels.filter((c) => has(c.name));
        if (srcHit || nodeHit || has(site.name) || channels.length > 0) {
          sources.push({ ...src, channels });
        }
      }
      if (nodeHit || has(site.name) || sources.length > 0) {
        nodes.push({ ...node, sources });
      }
    }
    if (has(site.name) || nodes.length > 0) out.push({ ...site, nodes });
  }
  return out;
}

function ChannelRow({
  ch,
}: {
  ch: TopologySource["channels"][number];
}) {
  return (
    <Link
      to={`/cameras/${ch.camera_logical_id}`}
      className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted"
      data-testid="topology-channel"
    >
      <span
        className="inline-block h-2 w-2 rounded-full"
        title={ch.live ? "live" : "idle"}
        style={{ backgroundColor: ch.live ? "#10b981" : "#9ca3af" }}
      />
      <VideoIcon className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="font-mono text-xs text-muted-foreground">
        ch{ch.channel_no}
      </span>
      <span className="truncate">{ch.name}</span>
      {ch.is_provisional && (
        <Badge variant="warning" className="ml-auto text-[10px]">
          provisional
        </Badge>
      )}
    </Link>
  );
}

function SourceCard({
  src,
  expandCmd,
  forceOpen = false,
}: {
  src: TopologySource;
  // Batch 1 — bumping `.v` applies expand-all/collapse-all (`.open`) to every
  // card without lifting all per-card state; `forceOpen` keeps a search-matched
  // NVR open so results are visible without a click.
  expandCmd?: { v: number; open: boolean };
  forceOpen?: boolean;
}) {
  // FE BUG 4 — per-NVR collapse. Collapsed by default so a fleet of 16-channel
  // NVRs doesn't make the page unusably long; the header (host/protocol/ch count)
  // stays visible and the chevron expands the channel list on demand.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (expandCmd) setExpanded(expandCmd.open);
  }, [expandCmd]);
  const show = expanded || forceOpen;
  return (
    <div className="rounded-md border bg-card p-2">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={show}
        data-testid="topology-source-toggle"
        className="flex w-full items-center gap-2 text-sm font-medium"
      >
        {show ? (
          <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
        )}
        <ServerIcon className="h-4 w-4 text-accent" />
        <span className="font-mono">{src.host}:{src.port}</span>
        <span className="text-xs text-muted-foreground">{src.protocol}</span>
        <Badge variant="offline" className="ml-auto text-[10px]">
          {src.channel_count} ch
        </Badge>
      </button>
      {show && (
        <div className="ml-2 mt-1 border-l pl-2">
          {src.channels.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">No channels.</p>
          ) : (
            src.channels.map((ch) => (
              <ChannelRow key={ch.camera_logical_id} ch={ch} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function TopologyPage() {
  const { data, isLoading, isError } = useTopology();
  const [search, setSearch] = useState("");
  // expand-all / collapse-all: bump `v` to broadcast `open` to every SourceCard.
  const [expandCmd, setExpandCmd] = useState<{ v: number; open: boolean }>();
  const searching = search.trim().length > 0;

  const sites = useMemo(
    () => filterTopology(data?.sites ?? [], search),
    [data, search],
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">NVR Topology</h1>
        <p className="text-sm text-muted-foreground">
          Sites, edge agents, NVRs and their channels.
        </p>
      </div>

      {data && data.sites.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[14rem] flex-1">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search NVR host, channel, agent, site…"
              className="h-8 pl-8 text-xs"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setExpandCmd((c) => ({ v: (c?.v ?? 0) + 1, open: true }))}
          >
            Expand all
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setExpandCmd((c) => ({ v: (c?.v ?? 0) + 1, open: false }))}
          >
            Collapse all
          </Button>
        </div>
      )}

      {isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="topology-loading">
          Loading topology…
        </p>
      )}
      {isError && (
        <p className="text-sm text-red-600" data-testid="topology-error">
          Failed to load topology.
        </p>
      )}
      {data && data.sites.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="topology-empty">
          No sources configured yet.
        </p>
      )}
      {data && data.sites.length > 0 && sites.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No sites, NVRs or channels match “{search}”.
        </p>
      )}

      {sites.map((site) => (
        <Card key={site.id}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HardDriveIcon className="h-4 w-4" />
              {site.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {site.nodes.length === 0 ? (
              <p className="text-xs text-muted-foreground">No edge nodes.</p>
            ) : (
              site.nodes.map((node) => (
                <div key={node.id} className="space-y-2">
                  <div className="text-sm font-medium">
                    {node.name}
                    {node.agent_name && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        agent: {node.agent_name}
                      </span>
                    )}
                  </div>
                  <div className="ml-2 space-y-2 border-l pl-3">
                    {node.sources.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No NVRs.</p>
                    ) : (
                      node.sources.map((src) => (
                        <SourceCard
                          key={src.id}
                          src={src}
                          expandCmd={expandCmd}
                          forceOpen={searching}
                        />
                      ))
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default TopologyPage;
